import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, RefreshCw } from "lucide-react";

/**
 * Phase 3 — Sales Approval (PM/Admin desktop).
 *
 * Shows pending promoter sales with the full submission detail and approve/reject
 * actions. Rejection requires a reason (server-enforced too). Once a sale is
 * decided, promoter-side edit/delete are blocked.
 */
export default function SalesApproval() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending_verification");
  const [viewing, setViewing] = useState(null);
  const [decision, setDecision] = useState(null); // {sale, type: 'approve'|'reject'}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/promoter/sales", {
        params: statusFilter === "__all__" ? {} : { status: statusFilter },
      });
      setRows(data || []);
    } catch (e) {
      toast.error("Could not load", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  return (
    <div className="space-y-4" data-testid="sales-approval-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Sales Approval</h1>
          <p className="text-sm text-slate-500 mt-1">Review and approve / reject promoter sales submissions.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-52" data-testid="approval-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_verification">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="__all__">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" onClick={load} data-testid="approval-refresh-btn">
            <RefreshCw size={14} className="mr-1" /> Refresh
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="approval-table">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2">Submitted</th>
                  <th className="text-left px-3 py-2">Promoter</th>
                  <th className="text-left px-3 py-2">Dealer</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Product</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Gross (incl. VAT)</th>
                  <th className="text-right px-3 py-2">Net (excl. VAT)</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-right px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={10} className="text-center py-6 text-slate-400">Loading…</td></tr>}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan={10} className="text-center py-6 text-slate-400" data-testid="approval-empty">
                    No sales in this filter.
                  </td></tr>
                )}
                {!loading && rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/60"
                      data-testid={`approval-row-${r.id.slice(0, 8)}`}>
                    <td className="px-3 py-2 text-xs font-mono">{(r.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2">{r.promoter_name}<div className="text-[10px] text-slate-400">{r.promoter_promoter_id}</div></td>
                    <td className="px-3 py-2 text-slate-600">{r.dealer_name || "—"}</td>
                    <td className="px-3 py-2">
                      <div>{r.customer_name}</div>
                      <div className="text-[10px] text-slate-500">{r.customer_phone}</div>
                    </td>
                    <td className="px-3 py-2">{r.brand} {r.product_model}</td>
                    <td className="px-3 py-2 text-right">{r.quantity}</td>
                    <td className="px-3 py-2 text-right text-slate-600">{Number(r.invoice_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-semibold" data-testid={`approval-net-${r.id.slice(0, 8)}`}>{Number(r.net_sales_amount || 0).toLocaleString()}</td>
                    <td className="px-3 py-2"><StatusPill status={r.status} /></td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => setViewing(r)}
                              data-testid={`approval-view-${r.id.slice(0, 8)}`}>
                        <Eye size={12} />
                      </Button>
                      {r.status === "pending_verification" && (
                        <>
                          <Button size="sm" className="ml-1 bg-emerald-700 hover:bg-emerald-800 text-white"
                                  onClick={() => setDecision({ sale: r, type: "approve" })}
                                  data-testid={`approval-approve-${r.id.slice(0, 8)}`}>
                            <CheckCircle2 size={12} />
                          </Button>
                          <Button size="sm" variant="outline" className="ml-1 text-rose-700 hover:bg-rose-50"
                                  onClick={() => setDecision({ sale: r, type: "reject" })}
                                  data-testid={`approval-reject-${r.id.slice(0, 8)}`}>
                            <XCircle size={12} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <SaleDetailDialog sale={viewing} onClose={() => setViewing(null)} />
      <DecisionDialog state={decision} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); }} />
    </div>
  );
}

function SaleDetailDialog({ sale, onClose }) {
  if (!sale) return null;
  return (
    <Dialog open={!!sale} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" data-testid="sale-detail-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-100 flex-shrink-0">
          <DialogTitle>{sale.invoice_number} — {sale.customer_name}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3" data-testid="sale-detail-scroll">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Row label="Promoter" value={`${sale.promoter_name} (${sale.promoter_promoter_id})`} />
            <Row label="Dealer / Counter" value={sale.dealer_name || "—"} />
            <Row label="Customer" value={`${sale.customer_name} · ${sale.customer_phone}`} />
            <Row label="Address" value={sale.customer_address || "—"} />
            <Row label="Brand / Category / Model" value={`${sale.brand} / ${sale.product_category || "—"} / ${sale.product_model}`} />
            <Row label="Serial #" value={sale.product_serial_number || "—"} />
            <Row label="Quantity" value={sale.quantity} />
            <Row label="Invoice" value={`${sale.invoice_number} · ${sale.invoice_date}`} />
            <Row label="Invoice Gross (incl. VAT)" value={`Rs. ${Number(sale.invoice_amount || 0).toLocaleString()}`} />
            <Row label={`VAT @ ${sale.vat_rate ? (sale.vat_rate*100).toFixed(0) : 13}%`} value={`Rs. ${Number(sale.vat_amount || 0).toLocaleString()}`} />
            <Row label="Net Sales (excl. VAT)" value={`Rs. ${Number(sale.net_sales_amount || 0).toLocaleString()}`} testId="detail-net-sales" />
            <Row label="Sale Date" value={sale.sale_date} />
            <Row label="Status" value={sale.status} />
            <Row label="Submitted At" value={(sale.created_at || "").slice(0, 19)} />
            <Row label="Remarks" value={sale.remarks || "—"} />
            {sale.status === "rejected" && <Row label="Rejection Reason" value={sale.rejection_reason || "—"} />}
            {sale.decided_by && <Row label="Decided By" value={`${sale.decided_by} · ${(sale.decided_at || "").slice(0, 19)}`} />}
          </div>
          <div className="text-[11px] text-slate-500 italic" data-testid="detail-vat-note">
            Target achievement and incentive slabs use Net Sales (excluding VAT). Gross and VAT are shown for reference only.
          </div>
          {sale.invoice_photo_url && (
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold text-slate-600">Invoice photo</div>
                <a href={sale.invoice_photo_url} target="_blank" rel="noopener noreferrer"
                   className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1"
                   data-testid="sale-detail-invoice-open">
                  <Eye size={12} /> Open full size
                </a>
              </div>
              <img src={sale.invoice_photo_url} alt="invoice"
                   className="w-full max-h-[60vh] object-contain rounded border bg-slate-50"
                   data-testid="sale-detail-invoice-img" />
            </div>
          )}
        </div>
        <DialogFooter className="px-6 py-3 border-t border-slate-100 flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="sale-detail-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-sm text-slate-900 break-words">{String(value)}</div>
    </div>
  );
}

function DecisionDialog({ state, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setReason(""); setRemarks(""); }, [state]);

  if (!state) return null;
  const { sale, type } = state;
  const isApprove = type === "approve";

  const submit = async () => {
    if (!isApprove && !reason.trim()) { toast.error("Rejection reason is required"); return; }
    setBusy(true);
    try {
      await api.patch(`/promoter/sales/${sale.id}/decide`, {
        decision: isApprove ? "approved" : "rejected",
        remarks: remarks || null,
        rejection_reason: isApprove ? null : reason,
      });
      toast.success(isApprove ? "Sale approved" : "Sale rejected");
      onDone();
    } catch (e) {
      toast.error("Could not decide", { description: formatApiErrorDetail(e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`decide-dialog-${type}`}>
        <DialogHeader>
          <DialogTitle>{isApprove ? "Approve sale" : "Reject sale"} — {sale.invoice_number}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-slate-600">
            {sale.promoter_name} · {sale.brand} {sale.product_model}
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded p-2 text-xs space-y-0.5">
            <div className="flex justify-between"><span>Invoice gross</span><span className="font-mono">Rs. {Number(sale.invoice_amount || 0).toLocaleString()}</span></div>
            <div className="flex justify-between"><span>VAT @ {sale.vat_rate ? (sale.vat_rate*100).toFixed(0) : 13}%</span><span className="font-mono">Rs. {Number(sale.vat_amount || 0).toLocaleString()}</span></div>
            <div className="flex justify-between font-semibold border-t border-slate-200 pt-0.5 mt-0.5"><span>Net (counts toward target)</span><span className="font-mono">Rs. {Number(sale.net_sales_amount || 0).toLocaleString()}</span></div>
          </div>
          {!isApprove && (
            <div className="space-y-1">
              <Label className="text-[11px] uppercase font-semibold text-slate-600">
                Rejection reason <span className="text-rose-600">*</span>
              </Label>
              <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is this sale being rejected?" data-testid="decide-reason" />
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-[11px] uppercase font-semibold text-slate-600">Remarks (optional)</Label>
            <Input value={remarks} onChange={(e) => setRemarks(e.target.value)} data-testid="decide-remarks" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="decide-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy}
                  className={isApprove ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-rose-700 hover:bg-rose-800 text-white"}
                  data-testid="decide-confirm">
            {busy ? "Saving..." : (isApprove ? "Confirm approve" : "Confirm reject")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  const cls = {
    pending_verification: "bg-amber-100 text-amber-800",
    approved: "bg-emerald-100 text-emerald-800",
    rejected: "bg-rose-100 text-rose-800",
    adjusted: "bg-indigo-100 text-indigo-800",
  }[status] || "bg-slate-100 text-slate-700";
  return <Badge className={cls}>{(status || "").replace("_", " ")}</Badge>;
}
